import React, { useState, useRef, useCallback, useEffect } from "react";
import DatePicker from "react-datepicker";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { PiWarningCircleLight } from "react-icons/pi";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";

interface DateTimePickerProps {
  onChange: (date: Date | null) => void;
  id?: string;
  label?: string;
  selected?: Date | null;
  placeholder?: string;
  required?: boolean;
  error?: string | boolean;
  helperText?: string;
  showTimeSelect?: boolean;
  timeIntervals?: number;
  dateFormat?: string;
  className?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  fullWidth?: boolean;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  id,
  label,
  selected,
  onChange,
  placeholder,
  required,
  error,
  helperText,
  showTimeSelect = true,
  timeIntervals = 15,
  dateFormat = "MMM d, yyyy h:mm aa",
  className,
  disabled,
  minDate,
  maxDate,
  fullWidth = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [workingDate, setWorkingDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<{ hours: number; minutes: number } | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const [shouldDropUp, setShouldDropUp] = useState(false);
  const portalRef = useRef<HTMLDivElement | null>(null);

  // Initialize working state when popover opens or selected date changes
  useEffect(() => {
    if (isOpen) {
      if (selected) {
        setWorkingDate(new Date(selected));
        setSelectedTime({
          hours: selected.getHours(),
          minutes: Math.floor(selected.getMinutes() / timeIntervals) * timeIntervals,
        });
      } else {
        setWorkingDate(null);
        setSelectedTime({ hours: 0, minutes: 0 });
      }
    }
  }, [isOpen, selected, timeIntervals]);

  const formatDateDisplay = useCallback(() => {
    if (!selected) return placeholder || t("Select date");
    const formatter = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(showTimeSelect && { hour: "numeric", minute: "2-digit", hour12: true }),
    });
    return formatter.format(selected);
  }, [selected, showTimeSelect, placeholder, t]);

  const updateMenuPosition = useCallback(() => {
    if (pickerRef.current && isOpen) {
      const rect = pickerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const spaceBelow = viewportHeight - rect.bottom;
      const pickerHeight = 400; // approximate height

      const shouldFlip = spaceBelow < pickerHeight && rect.top > pickerHeight;
      setShouldDropUp(shouldFlip);

      // Ensure enough width for calendar + time picker side-by-side
      const minWidth = showTimeSelect ? 420 : rect.width;
      const portalWidth = Math.max(rect.width, minWidth);
      // Clamp left so the picker doesn't overflow the viewport edge
      const left = Math.max(8, Math.min(rect.left, viewportWidth - portalWidth - 16));

      setMenuPosition({
        top: shouldFlip ? rect.top - pickerHeight : rect.bottom,
        left,
        width: portalWidth,
      });
    }
  }, [isOpen, showTimeSelect]);

  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();
      const handleScroll = () => updateMenuPosition();
      const handleResize = () => updateMenuPosition();

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is within picker header OR within portal content
      const isClickInPickerHeader = pickerRef.current && pickerRef.current.contains(target);
      const isClickInPortal = portalRef.current && portalRef.current.contains(target);

      // Only close if click is outside both the header and the portal
      if (!isClickInPickerHeader && !isClickInPortal) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleTimeChange = (hours?: number, minutes?: number) => {
    const newTime = {
      hours: hours !== undefined ? hours : (selectedTime?.hours || 0),
      minutes: minutes !== undefined ? minutes : (selectedTime?.minutes || 0),
    };
    setSelectedTime(newTime);

    // Update working date with new time
    if (workingDate) {
      const updatedDate = new Date(workingDate);
      updatedDate.setHours(newTime.hours, newTime.minutes, 0);
      setWorkingDate(updatedDate);
    }
  };

  const handleDateSelect = (date: Date | null) => {
    if (date) {
      // Update working date with the selected date
      const currentTime = selectedTime || { hours: 0, minutes: 0 };
      date.setHours(currentTime.hours, currentTime.minutes, 0);
      setWorkingDate(date);

      // Only close if time select is not shown (date-only picker)
      if (!showTimeSelect) {
        onChange(date);
        setIsOpen(false);
      }
      // If time select is shown, keep popover open and wait for user to select time and confirm
    }
  };

  const handleConfirm = () => {
    if (workingDate) {
      onChange(workingDate);
      setIsOpen(false);
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 / timeIntervals }, (_, i) => i * timeIntervals);

  return (
    <div className="flex flex-col">
      {(label || required) && (
        <div className="mb-2 flex items-center gap-1">
          {label && (
            <label htmlFor={id} className="block text-xs text-gray-700 dark:text-gray-300">
              {t(label)}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
        </div>
      )}

      <div className="relative" ref={pickerRef}>
        <div
          className={clsx(
            "cursor-pointer flex justify-between items-center px-3 py-2",
            fullWidth ? "w-full" : "w-full",
            "bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs",
            disabled && "opacity-50 cursor-not-allowed",
            error && "border-red-500",
            className
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              setIsOpen(!isOpen);
            }
          }}
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-xs truncate text-gray-900 dark:text-white">
              {formatDateDisplay()}
            </span>
          </div>
          <svg
            className={clsx(
              "text-gray-500 dark:text-gray-400 w-4 h-4 flex-shrink-0 transition-transform",
              isOpen && "rotate-180"
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {isOpen &&
          createPortal(
            <motion.div
              ref={portalRef}
              initial={{ opacity: 0, y: shouldDropUp ? 10 : -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: shouldDropUp ? 10 : -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="rounded-md bg-white dark:bg-gray-800 shadow-lg z-[300000001]"
              style={{
                position: "fixed",
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                width: `${menuPosition.width}px`,
              }}
            >
              <div className="p-4 flex gap-4">
                {/* Calendar Section */}
                <div>
                  <DatePicker
                    selected={workingDate}
                    onChange={handleDateSelect}
                    inline
                    minDate={minDate}
                    maxDate={maxDate}
                    wrapperClassName="react-datepicker-wrapper-inline"
                    calendarClassName="dark:bg-gray-800 dark:text-white"
                  />
                </div>

                {/* Time Picker Section */}
                {showTimeSelect && (
                  <div className="flex flex-col gap-4 pt-2">
                    <div className="text-xs text-gray-700 dark:text-gray-300">
                      {t("Time")}
                    </div>

                    {/* Hours */}
                    <div className="flex flex-col gap-2">
                      <label className="text-xs text-gray-600 dark:text-gray-400">{t("Hours")}</label>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded">
                        {hours.map((hour) => (
                          <button
                            key={`hour-${hour}`}
                            type="button"
                            className={clsx(
                              "w-12 px-3 py-1.5 text-xs border-b border-gray-200 dark:border-gray-700 transition-colors",
                              selectedTime?.hours === hour
                                ? "bg-primary-500 text-white dark:bg-primary-600"
                                : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600"
                            )}
                            onClick={() => handleTimeChange(hour, undefined)}
                          >
                            {String(hour).padStart(2, "0")}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Minutes */}
                    <div className="flex flex-col gap-2">
                      <label className="text-xs text-gray-600 dark:text-gray-400">{t("Minutes")}</label>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded">
                        {minutes.map((minute) => (
                          <button
                            key={`minute-${minute}`}
                            type="button"
                            className={clsx(
                              "w-12 px-3 py-1.5 text-xs border-b border-gray-200 dark:border-gray-700 transition-colors",
                              selectedTime?.minutes === minute
                                ? "bg-primary-500 text-white dark:bg-primary-600"
                                : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600"
                            )}
                            onClick={() => handleTimeChange(undefined, minute)}
                          >
                            {String(minute).padStart(2, "0")}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm/Close Button */}
              <div className="border-t border-gray-200 dark:border-gray-700 p-3 flex justify-end gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => {
                    if (showTimeSelect) {
                      handleConfirm();
                    } else {
                      setIsOpen(false);
                    }
                  }}
                >
                  {t(showTimeSelect ? "Confirm" : "Close")}
                </button>
              </div>
            </motion.div>,
            document.body
          )}
      </div>

      {error && helperText && (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
          <PiWarningCircleLight className="h-4 w-4 text-red-500 flex-shrink-0" />
          <span>{helperText}</span>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
